import React, { useState, useMemo } from 'react'
import { Search, UserCheck, UserX } from 'lucide-react'
import { TeamMember, RoleWithMember } from '../../services/orgChartService'
import { PersonCard } from './PersonCard'
import { EditMemberModal } from './EditMemberModal'

interface DirectoryViewProps {
  teamMembers: TeamMember[]
  roles: RoleWithMember[]
  isAdmin: boolean
  onMemberUpdated?: () => Promise<void> | void
}

export const DirectoryView: React.FC<DirectoryViewProps> = ({
  teamMembers,
  roles,
  isAdmin,
  onMemberUpdated
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterMode, setFilterMode] = useState<'all' | 'assigned' | 'unassigned'>('all')
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null)
  /** Admins can reveal hidden members to edit the “hide” flag again */
  const [showHiddenMembers, setShowHiddenMembers] = useState(false)

  const membersForDirectory = useMemo(() => {
    if (isAdmin && showHiddenMembers) return teamMembers
    return teamMembers.filter(m => !m.hidden)
  }, [teamMembers, isAdmin, showHiddenMembers])

  // Build a map of memberId -> roles they hold
  const memberRolesMap = useMemo(() => {
    const map = new Map<string, RoleWithMember[]>()
    for (const role of roles) {
      if (role.assignedMemberId) {
        if (!map.has(role.assignedMemberId)) {
          map.set(role.assignedMemberId, [])
        }
        map.get(role.assignedMemberId)!.push(role)
      }
    }
    return map
  }, [roles])

  // Filter and search members
  const filteredMembers = useMemo(() => {
    let result = membersForDirectory

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      result = result.filter(member =>
        member.name.fullName.toLowerCase().includes(search) ||
        member.primaryEmail.toLowerCase().includes(search) ||
        (member.jobTitle && member.jobTitle.toLowerCase().includes(search))
      )
    }

    // Apply assignment filter
    if (filterMode === 'assigned') {
      result = result.filter(member => memberRolesMap.has(member.id))
    } else if (filterMode === 'unassigned') {
      result = result.filter(member => !memberRolesMap.has(member.id))
    }

    // Sort by name
    result.sort((a, b) => a.name.fullName.localeCompare(b.name.fullName))

    return result
  }, [membersForDirectory, searchTerm, filterMode, memberRolesMap])

  const assignedCount = membersForDirectory.filter(m => memberRolesMap.has(m.id)).length
  const unassignedCount = membersForDirectory.length - assignedCount
  const hiddenCount = teamMembers.filter(m => m.hidden).length

  return (
    <div>
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or job title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        {/* Filter buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && hiddenCount > 0 && (
            <label className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-amber-200 bg-amber-50 text-amber-900 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                checked={showHiddenMembers}
                onChange={(e) => setShowHiddenMembers(e.target.checked)}
              />
              <span>Show hidden ({hiddenCount})</span>
            </label>
          )}
          <button
            onClick={() => setFilterMode('all')}
            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
              filterMode === 'all'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All ({membersForDirectory.length})
          </button>
          <button
            onClick={() => setFilterMode('assigned')}
            className={`flex items-center space-x-1 px-3 py-2 text-sm rounded-lg transition-colors ${
              filterMode === 'assigned'
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <UserCheck className="w-4 h-4" />
            <span>Assigned ({assignedCount})</span>
          </button>
          <button
            onClick={() => setFilterMode('unassigned')}
            className={`flex items-center space-x-1 px-3 py-2 text-sm rounded-lg transition-colors ${
              filterMode === 'unassigned'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <UserX className="w-4 h-4" />
            <span>Unassigned ({unassignedCount})</span>
          </button>
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-gray-500 mb-4">
        Showing {filteredMembers.length} of {membersForDirectory.length} team members
        {isAdmin && !showHiddenMembers && hiddenCount > 0 && (
          <span className="text-amber-700"> ({hiddenCount} hidden)</span>
        )}
      </p>

      {/* Members grid */}
      {filteredMembers.length === 0 ? (
        <div className="text-center py-12">
          <UserX className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No team members found</p>
          {isAdmin && teamMembers.length > 0 && membersForDirectory.length === 0 && (
            <p className="text-sm text-amber-700 mt-2">
              All synced members are hidden from this view. Enable &quot;Show hidden&quot; above to edit them.
            </p>
          )}
          {membersForDirectory.length === 0 && isAdmin && teamMembers.length === 0 && (
            <p className="text-sm text-gray-400 mt-2">
              Click "Sync Directory" to load team members from Google Directory
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredMembers.map(member => (
            <PersonCard
              key={member.id}
              member={member}
              roles={memberRolesMap.get(member.id) || []}
              showEditButton={isAdmin}
              onEdit={() => setEditingMember(member)}
              showExcludedFromDirectoryBadge={isAdmin && showHiddenMembers && member.hidden === true}
            />
          ))}
        </div>
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <EditMemberModal
          member={editingMember}
          isOpen={true}
          onClose={() => setEditingMember(null)}
          onSave={async () => {
            if (onMemberUpdated) {
              await onMemberUpdated()
            }
          }}
        />
      )}
    </div>
  )
}
